'use client';

/**
 * SellerRecentActivityCard — recent submissions + buyer messages (rail).
 *
 * Bottom rail card on the seller landing. Two zones:
 *
 *   1. Submission status — last N RFQs in `state='done'` (response
 *      submitted), descending by deadline as a proxy for recency
 *      until the BFF surfaces a proper `submitted_at` field.
 *   2. Inbound messages — placeholder for now ("coming soon"). The
 *      proper read needs an aggregated "messages addressed to RFQs
 *      I'm invited to" endpoint; today's `chat` strategy is per-
 *      entity, so an N-RFQ aggregation would mean N parallel calls.
 *      Worth a focused BFF strategy when this becomes the bottleneck.
 *
 * @see klyde_edge/ui_config/composition/cards/seller.py
 *      content_key="seller_recent_activity"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, MessageSquare, Loader2 } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useSellerLabel } from '@/lib/stores/total-matrix-store';
import { api } from '@/lib/api/client';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';
import type { VPRfqSummary, VPRfqListResponse } from '@/lib/api/types';

export function SellerRecentActivityCard() {
  const selectedUser = useDevUserStore((s) => s.selectedUser);
  const listStrategy = useCapabilityStore((s) => s.strategyName('vendor_portal', 'list'));
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);

  // Server-driven labels.
  const labelSubmittedHeader = useSellerLabel('recent_section_submitted');
  const labelMessagesHeader = useSellerLabel('recent_section_messages');
  const labelLoading = useSellerLabel('recent_loading');
  const labelNoSubmissions = useSellerLabel('recent_no_submissions');
  const labelMessagesPlaceholder = useSellerLabel('recent_messages_placeholder');
  const labelNoProject = useSellerLabel('rfq_card_no_project');

  const [rfqs, setRfqs] = useState<VPRfqSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedUser || !listStrategy) return;
    let cancelled = false;
    setIsLoading(true);
    api
      .getStrategyData<VPRfqListResponse>(listStrategy, {})
      .then((res) => {
        if (cancelled || !res.success) return;
        setRfqs(res.data?.rfqs ?? []);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUser, listStrategy]);

  const submitted = useMemo(() => {
    return rfqs
      .filter((r) => r.state === 'done')
      .sort((a, b) => {
        const ta = a.deadline ? Date.parse(a.deadline) : 0;
        const tb = b.deadline ? Date.parse(b.deadline) : 0;
        return tb - ta;
      })
      .slice(0, 5);
  }, [rfqs]);

  return (
    <RailCard title="Recent" icon={MessageSquare}>
      <div className={cn(text.eyebrow, 'mb-1.5 px-1')}>{labelSubmittedHeader}</div>
      {isLoading && submitted.length === 0 ? (
        <div className={cn(text.muted, 'flex items-center gap-1.5 px-1 pb-2')}>
          <Loader2 className="h-3 w-3 animate-spin" />
          {labelLoading}
        </div>
      ) : submitted.length === 0 ? (
        <div className={cn(text.muted, 'px-1 pb-2')}>{labelNoSubmissions}</div>
      ) : (
        <div className="-mx-3">
          {submitted.map((rfq) => (
            <button
              key={rfq.rfq_sid}
              onClick={() => {
                selectNode({ id: rfq.rfq_sid, label: rfq.reference ?? rfq.rfq_sid, type: 'rfq' });
                setExplorerSelection('rfq');
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/40 transition-colors"
            >
              <CheckCircle2 className="h-3 w-3 shrink-0 text-green-600 dark:text-green-400" />
              <div className="flex-1 min-w-0">
                <div className={cn(text.body, 'truncate')}>{rfq.reference ?? '—'}</div>
                <div className={cn(text.muted, 'truncate')}>{rfq.project_name ?? labelNoProject}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className={cn(text.eyebrow, 'mt-3 mb-1.5 px-1')}>{labelMessagesHeader}</div>
      <div className={cn(text.muted, 'px-1 pb-1 italic opacity-70')}>
        {labelMessagesPlaceholder}
      </div>
    </RailCard>
  );
}
